import { Component, Input, OnInit, SimpleChange } from '@angular/core';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { round } from 'lodash';
import { ActionCenterService } from 'src/app/features/action-center/service/action-center.service';
import { ActionData, ActionLocationInfo, ActionState, ActionStatus } from 'src/app/features/action-center/types/action-center-types';
import { OpportunityTypes } from 'src/app/features/action-center/types/constants';
import { PageStateModel } from 'src/app/features/action-center/types/page-state-model';
import { eroiService } from 'src/app/features/total-value-delivery/services/eroi-metric.service';
import { eroiIds } from 'src/app/features/total-value-delivery/types/tvd-types';

@Component({
    selector: 'app-table-row',
    templateUrl: './table-row.component.html',
    styleUrls: ['./table-row.component.scss'],
    standalone: false
})
export class TableRowComponent implements OnInit {

    displayLoaderFlag: boolean = false;
    isMoved: boolean = false;
    updatedActionState = ActionState.MyActions;

    statusColors: Map<ActionStatus, string> = new Map([
        [ActionStatus.NoActionYet, 'yellow'],
        [ActionStatus.CustomerAccepted, 'blue'],
        [ActionStatus.PitchedToCustomer, 'purple'],
        [ActionStatus.Completed, 'green'],
        [ActionStatus.EnteredInESS, 'teal'],
        [ActionStatus.ActionInProgress, 'gray'],
    ]);

    statusClass(status: any): string | string[] | Set<string> | { [klass: string]: any; } {
        return this.statusColors.get(status) ?? '';
    }

    @Input() currPageState: ActionState | undefined;
    @Input() dataRow: ActionData | undefined;
    @Input() opportunityType: string = 'sales';
    @Input() header: boolean = false;

    showDialogBox: boolean = false;

    pageData: PageStateModel | undefined;
    totalOpportunitySales: number = 0;
    totalOpportunityTVD: number = 0;
    locationPopupData: ActionLocationInfo[] = [];

    grayHex = '#E0E0E0'
    orangeHex: string = '#F17A22';
    greenHex: string = '#39B54A';

    constructor(private actionCenterService: ActionCenterService, private _eroiService: eroiService) { }

    openLocationBox(locationsToDisplay: ActionLocationInfo[]) {
        this.locationPopupData = locationsToDisplay;
        this.showDialogBox = true;
    }

    closeLocationBox() {
        this.showDialogBox = false;
    }

    exportToExcel(locations: ActionLocationInfo[]) {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');

        let fileName = `${year}${month}${day}-${this.dataRow!.recId}-${this.currPageState}List`;

        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(locations);
        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const excelData: Blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        FileSaver.saveAs(excelData, fileName + '.xlsx');
    }

    ngOnInit(): void {
        if (!this.header) {
            // opportunityType should be one of the values in the OpportunityTypes array
            if (OpportunityTypes.indexOf(this.opportunityType ?? "") === -1) {
                throw new Error('Opportunity Type is required');
            }

            this.pageData = this.actionCenterService.pageData.value
            this.totalOpportunityTVD = this.pageData.totalTVDOpportunity ?? 0;
            this.totalOpportunitySales = this.pageData.actions.reduce((acc, curr) => acc + curr.groupedSalesOpportunity, 0);
        }
    }

    ngOnChanges(changes: SimpleChange): void {
        // opportunityType should be one of the values in the OpportunityTypes array
        if (OpportunityTypes.indexOf(this.opportunityType ?? "") === -1) {
            throw new Error('Opportunity Type is required');
        }

        this.pageData = this.actionCenterService.pageData.value
        this.totalOpportunityTVD = this.pageData?.totalTVDOpportunity ?? 0;
        this.totalOpportunitySales = this.pageData.actions.reduce((acc, curr) => acc + curr.groupedSalesOpportunity, 0);
    }

    displayDate(date: string) {
        // date string format: 2021-07-01T00:00:00.000Z
        const dateObj = new Date(date);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
        return dateObj.toLocaleDateString('en-US', options);
    }

    mapOpportunityTypeToCostValue(opportunityType: string): string {
        if (this.dataRow === undefined) {
            return '';
        }
        switch (opportunityType) {
            case 'sales':
                return this.dataRow.groupedSalesOpportunity.toString();
            case eroiIds.PRODUCTIVITY:
                return this.dataRow.groupedTVDOpportunity.totalProductivityDollars.toString();
            case eroiIds.ASSETS:
                return (this.dataRow.groupedTVDOpportunity.totalReplacementDollars + this.dataRow.groupedTVDOpportunity.totalRepairDollars).toString();
            case eroiIds.EMPLOYEESAFETY:
                return "0";
            case eroiIds.WATER:
                return this.dataRow.groupedTVDOpportunity.totalWaterDollars.toString();
            case eroiIds.ENERGY:
                return this.dataRow.groupedTVDOpportunity.totalEnergyDollars.toString();
            case eroiIds.GREENHOUSEGASES:
                return "0";
            case eroiIds.PLASTIC:
                return this.dataRow.groupedTVDOpportunity.totalPlasticWasteDollars.toString();
            case eroiIds.CARDBOARD:
                return this.dataRow.groupedTVDOpportunity.totalCardboardWasteDollars.toString();
            case eroiIds.COSTAVOIDANCE:
                return this.dataRow.groupedTVDOpportunity.totalCostAvoidance.toString();
            default:
                return '';
        }
    }

    mapOpportunityTypeToValue(opportunityType: string): string {
        if (this.dataRow === undefined) {
            return '';
        }
        switch (opportunityType) {
            case 'sales':
                return round(this.totalOpportunitySales != 0 ? (this.dataRow.groupedSalesOpportunity / this.totalOpportunitySales) * 100 : 0, 2).toString();
            case eroiIds.PRODUCTIVITY:
                return this.dataRow.groupedTVDOpportunity.totalProductivity.toString();
            case eroiIds.ASSETS:
                return "0";
            case eroiIds.EMPLOYEESAFETY:
                return this.dataRow.groupedTVDOpportunity.totalEmployeeSafety.toString();
            case eroiIds.WATER:
                return this.dataRow.groupedTVDOpportunity.totalWater.toString();
            case eroiIds.ENERGY:
                return this.dataRow.groupedTVDOpportunity.totalEnergy.toString();
            case eroiIds.GREENHOUSEGASES:
                return this.dataRow.groupedTVDOpportunity.totalAir.toString();
            case eroiIds.PLASTIC:
                return this.dataRow.groupedTVDOpportunity.totalPlasticWaste.toString();
            case eroiIds.CARDBOARD:
                return this.dataRow.groupedTVDOpportunity.totalCardboardWaste.toString();
            case eroiIds.COSTAVOIDANCE:
                return "0";
            default:
                return '';
        }
    }

    mapOpportunityTypeToUnits(opportunityType: string): string {
        if (this.dataRow === undefined) {
            return '';
        }
        switch (opportunityType) {
            case 'sales':
                return '%';
            case eroiIds.PRODUCTIVITY:
            case eroiIds.ASSETS:
            case eroiIds.EMPLOYEESAFETY:
            case eroiIds.WATER:
            case eroiIds.ENERGY:
            case eroiIds.GREENHOUSEGASES:
            case eroiIds.PLASTIC:
            case eroiIds.CARDBOARD:
            case eroiIds.COSTAVOIDANCE:
                return this._eroiService.mapIdToUnits(opportunityType);
            default:
                return '';
        }
    }

    isTvdType(opportunityType: string): boolean {
        return [
            eroiIds.PRODUCTIVITY,
            eroiIds.ASSETS,
            eroiIds.EMPLOYEESAFETY,
            eroiIds.WATER,
            eroiIds.ENERGY,
            eroiIds.GREENHOUSEGASES,
            eroiIds.PLASTIC,
            eroiIds.CARDBOARD,
            eroiIds.COSTAVOIDANCE
        ].includes(opportunityType as eroiIds);
    }

    actionMoved($event: ActionState) {
        this.isMoved = true;
        this.updatedActionState = $event;
    }

    displayMovedAlert(): string | undefined {
        return `Action ${this.dataRow?.productName} moved to ${this.updatedActionState} successfully`;
    }

    displayRowLoader($event: boolean) {
        this.displayLoaderFlag = $event;
    }
}
